import { Injectable } from '@angular/core';
import { ChartData } from '../chart.interface';
import { Time } from 'lightweight-charts';

@Injectable({
  providedIn: 'root'
})
export class TechnicalIndicatorsService {

  /**
   * Calculate Simple Moving Average (SMA)
   */
  calculateSMA(data: ChartData[], period: number): { time: Time; value: number }[] {
    // Null safety checks
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('[WARNING] Invalid or empty data array provided to calculateSMA');
      return [];
    }

    if (!period || typeof period !== 'number' || period <= 0 || !Number.isInteger(period)) {
      console.warn('[WARNING] Invalid period provided to calculateSMA:', period);
      return [];
    }

    if (data.length < period) {
      console.warn(`[WARNING] Insufficient data points for SMA calculation. Required: ${period}, Available: ${data.length}`);
      return [];
    }

    const smaData: { time: Time; value: number }[] = [];

    for (let i = period - 1; i < data.length; i++) {
      const slice = data.slice(i - period + 1, i + 1);

      // Validate slice data
      const validSlice = slice.filter(item =>
        item &&
        typeof item === 'object' &&
        typeof item.close === 'number' &&
        !isNaN(item.close) &&
        item.time !== undefined
      );

      if (validSlice.length !== slice.length) {
        console.warn(`[WARNING] Invalid data points found in SMA calculation at index ${i}`);
        continue;
      }

      const sum = validSlice.reduce((acc, item) => acc + item.close, 0);
      const average = sum / period;

      smaData.push({
        time: data[i].time,
        value: average
      });
    }

    return smaData;
  }

  /**
   * Calculate Average True Range (ATR)
   */
  calculateATR(data: ChartData[], period: number): { time: Time; value: number }[] {
    try {
      // Null safety checks
      if (!Array.isArray(data) || data.length === 0) {
        console.warn('[WARNING] Invalid or empty data array provided to calculateATR');
        return [];
      }

      if (!period || typeof period !== 'number' || period <= 0 || !Number.isInteger(period)) {
        console.warn('[WARNING] Invalid period provided to calculateATR:', period);
        return [];
      }

      if (data.length < period) {
        console.warn(`[WARNING] Insufficient data points for ATR calculation. Required: ${period}, Available: ${data.length}`);
        return [];
      }

      const trueRanges: number[] = [];
      const atrData: { time: Time; value: number }[] = [];

      // Calculate True Range for each period
      for (let i = 1; i < data.length; i++) {
        const current = data[i];
        const previous = data[i - 1];

        // Validate data points
        if (!current || !previous ||
          typeof current.high !== 'number' || typeof current.low !== 'number' ||
          typeof previous.close !== 'number' || isNaN(current.high) || isNaN(current.low) || isNaN(previous.close)) {
          console.warn(`[WARNING] Invalid data points found in ATR calculation at index ${i}`);
          continue;
        }

        const tr1 = current.high - current.low;
        const tr2 = Math.abs(current.high - previous.close);
        const tr3 = Math.abs(current.low - previous.close);

        const trueRange = Math.max(tr1, tr2, tr3);
        trueRanges.push(trueRange);
      }

      // Calculate ATR using exponential moving average
      for (let i = period - 1; i < trueRanges.length; i++) {
        if (i === period - 1) {
          // Initial ATR is simple average of first 'period' TR values
          const sum = trueRanges.slice(0, period).reduce((acc, tr) => acc + tr, 0);
          const atr = sum / period;
          atrData.push({
            time: data[i + 1].time,
            value: atr
          });
        } else {
          // Subsequent ATR values use exponential smoothing
          const previousATR = atrData[atrData.length - 1].value;
          const currentTR = trueRanges[i];
          const atr = (previousATR * (period - 1) + currentTR) / period;
          atrData.push({
            time: data[i + 1].time,
            value: atr
          });
        }
      }

      return atrData;
    } catch (error) {
      console.error('[ERROR] Error calculating ATR:', error);
      return [];
    }
  }

  /**
   * Calculate Relative Strength Index (RSI) using Wilder's smoothing method
   */
  calculateRSI(data: ChartData[], period: number = 14): { time: Time; value: number }[] {
    if (data.length < period + 1) {
      return [];
    }

    const rsiData: { time: Time; value: number }[] = [];
    let avgGain = 0;
    let avgLoss = 0;

    // Calculate initial gains and losses
    const gains: number[] = [];
    const losses: number[] = [];

    for (let i = 1; i < data.length; i++) {
      const change = data[i].close - data[i - 1].close;
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? Math.abs(change) : 0);
    }

    // Calculate initial averages (simple average for first period)
    for (let i = 0; i < period; i++) {
      avgGain += gains[i];
      avgLoss += losses[i];
    }
    avgGain /= period;
    avgLoss /= period;

    // Calculate first RSI value
    const rs1 = avgLoss === 0 ? 100 : avgGain / avgLoss;
    const rsi1 = 100 - (100 / (1 + rs1));

    rsiData.push({
      time: data[period].time, // Use the time of the period+1 data point
      value: Math.max(0, Math.min(100, rsi1)) // Clamp between 0 and 100
    });

    // Calculate subsequent RSI values using Wilder's smoothing (modified EMA)
    // Alpha = 1/period for Wilder's smoothing
    const alpha = 1 / period;

    for (let i = period; i < gains.length; i++) {
      // Wilder's smoothing: new_avg = alpha * current_value + (1 - alpha) * previous_avg
      avgGain = alpha * gains[i] + (1 - alpha) * avgGain;
      avgLoss = alpha * losses[i] + (1 - alpha) * avgLoss;

      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - (100 / (1 + rs));

      rsiData.push({
        time: data[i + 1].time,
        value: Math.max(0, Math.min(100, rsi)) // Clamp between 0 and 100
      });
    }

    return rsiData;
  }

  /**
   * Calculate Smoothed RSI (applies additional EMA smoothing to reduce noise)
   */
  calculateSmoothedRSI(data: ChartData[], rsiPeriod: number = 14, smoothPeriod: number = 3): { time: Time; value: number }[] {
    // First calculate regular RSI
    const rsiData = this.calculateRSI(data, rsiPeriod);

    if (rsiData.length < smoothPeriod) {
      return rsiData;
    }

    // Apply EMA smoothing to the RSI values
    const smoothedRSI: { time: Time; value: number }[] = [];
    const multiplier = 2 / (smoothPeriod + 1);

    // First smoothed value is just the first RSI value
    smoothedRSI.push(rsiData[0]);

    // Apply EMA smoothing to subsequent values
    for (let i = 1; i < rsiData.length; i++) {
      const smoothedValue = (rsiData[i].value * multiplier) + (smoothedRSI[i - 1].value * (1 - multiplier));
      smoothedRSI.push({
        time: rsiData[i].time,
        value: Math.max(0, Math.min(100, smoothedValue)) // Clamp between 0 and 100
      });
    }

    return smoothedRSI;
  }

  /**
   * Calculate Bollinger Bands
   */
  calculateBollingerBands(data: ChartData[], period: number = 20, stdDev: number = 2): {
    time: Time;
    middle: number;
    upper: number;
    lower: number;
  }[] {
    if (data.length < period) {
      return [];
    }

    const bollingerData: { time: Time; middle: number; upper: number; lower: number }[] = [];

    for (let i = period - 1; i < data.length; i++) {
      const slice = data.slice(i - period + 1, i + 1);
      const sum = slice.reduce((acc, item) => acc + item.close, 0);
      const sma = sum / period;

      // Calculate standard deviation
      const squaredDiffs = slice.map(item => Math.pow(item.close - sma, 2));
      const variance = squaredDiffs.reduce((acc, diff) => acc + diff, 0) / period;
      const standardDeviation = Math.sqrt(variance);

      bollingerData.push({
        time: data[i].time,
        middle: sma,
        upper: sma + (stdDev * standardDeviation),
        lower: sma - (stdDev * standardDeviation)
      });
    }

    return bollingerData;
  }

  /**
   * Calculate MACD (Moving Average Convergence Divergence)
   */
  calculateMACD(data: ChartData[], fastPeriod: number = 12, slowPeriod: number = 26, signalPeriod: number = 9): {
    time: Time;
    macd: number;
    signal: number;
    histogram: number;
  }[] {
    if (data.length < slowPeriod) {
      return [];
    }

    const fastEMA = this.calculateEMA(data, fastPeriod);
    const slowEMA = this.calculateEMA(data, slowPeriod);

    // Build a time-indexed map of the fast EMA for alignment
    const fastEMAMap = new Map<string, number>();
    fastEMA.forEach(item => fastEMAMap.set(String(item.time), item.value));

    const macdLine: { time: Time; value: number }[] = [];

    // MACD = Fast EMA - Slow EMA, aligned by time
    for (const slowItem of slowEMA) {
      const fastValue = fastEMAMap.get(String(slowItem.time));
      if (fastValue !== undefined) {
        macdLine.push({
          time: slowItem.time,
          value: fastValue - slowItem.value
        });
      }
    }

    if (macdLine.length < signalPeriod) {
      return [];
    }

    // Signal line = EMA of MACD line
    const signalLine = this.calculateEMAFromValues(macdLine, signalPeriod);

    // Build time-indexed map of signal line
    const signalMap = new Map<string, number>();
    signalLine.forEach(item => signalMap.set(String(item.time), item.value));

    // Histogram = MACD - Signal, aligned by time
    const macdData: { time: Time; macd: number; signal: number; histogram: number }[] = [];

    for (const macdItem of macdLine) {
      const sigValue = signalMap.get(String(macdItem.time));
      if (sigValue !== undefined) {
        macdData.push({
          time: macdItem.time,
          macd: macdItem.value,
          signal: sigValue,
          histogram: macdItem.value - sigValue
        });
      }
    }

    return macdData;
  }

  /**
   * Calculate Exponential Moving Average (EMA)
   */
  calculateEMA(data: ChartData[], period: number): { time: Time; value: number }[] {
    if (data.length < period) {
      return [];
    }

    const emaData: { time: Time; value: number }[] = [];
    const multiplier = 2 / (period + 1);

    // Start with SMA for the first value
    const initialSum = data.slice(0, period).reduce((acc, item) => acc + item.close, 0);
    const initialEMA = initialSum / period;

    emaData.push({
      time: data[period - 1].time,
      value: initialEMA
    });

    // Calculate EMA for subsequent values
    for (let i = period; i < data.length; i++) {
      const ema = (data[i].close * multiplier) + (emaData[emaData.length - 1].value * (1 - multiplier));
      emaData.push({
        time: data[i].time,
        value: ema
      });
    }

    return emaData;
  }

  /**
   * Calculate EMA from existing values (used for signal line in MACD)
   */
  private calculateEMAFromValues(data: { time: Time; value: number }[], period: number): { time: Time; value: number }[] {
    if (data.length < period) {
      return [];
    }

    const emaData: { time: Time; value: number }[] = [];
    const multiplier = 2 / (period + 1);

    // Start with SMA for the first value
    const initialSum = data.slice(0, period).reduce((acc, item) => acc + item.value, 0);
    const initialEMA = initialSum / period;

    emaData.push({
      time: data[period - 1].time,
      value: initialEMA
    });

    // Calculate EMA for subsequent values
    for (let i = period; i < data.length; i++) {
      const ema = (data[i].value * multiplier) + (emaData[emaData.length - 1].value * (1 - multiplier));
      emaData.push({
        time: data[i].time,
        value: ema
      });
    }

    return emaData;
  }

  /**
   * Apply ATR-based candlestick coloring
   */
  applyATRBasedColoring(data: ChartData[], atrData: { time: Time; value: number }[], period: number): ChartData[] {
    const coloredData = [...data];

    for (let i = 0; i < coloredData.length; i++) {
      const atrValue = atrData.find(atr => atr.time === coloredData[i].time);
      if (atrValue) {
        const priceRange = coloredData[i].high - coloredData[i].low;
        const atrThreshold = atrValue.value * 1.5; // 150% of ATR

        if (priceRange > atrThreshold) {
          // Mark as high volatility with custom color
          (coloredData[i] as any).color = '#FF6B35'; // Orange for high volatility
        }
      }
    }

    return coloredData;
  }

  /**
   * Helper method to get implied average gain from RSI
   */
  private getImpliedAvgGain(rsi: number, period: number): number {
    const rs = (100 - rsi) / rsi;
    return rs / (period - 1);
  }

  /**
   * Helper method to get implied average loss from RSI
   */
  private getImpliedAvgLoss(rsi: number, period: number): number {
    const rs = (100 - rsi) / rsi;
    return 1 / (period - 1);
  }

  /**
   * Get SMA color based on period
   */
  getSMAColor(period: number): string {
    const colors: { [key: number]: string } = {
      20: '#FF6B35',  // Orange
      50: '#4ECDC4',  // Teal
      100: '#45B7D1', // Blue
      200: '#96CEB4'  // Green
    };
    return colors[period] || '#666666';
  }

  /**
   * Calculate Stochastic Oscillator (%K and %D)
   */
  calculateStochastic(data: ChartData[], kPeriod: number = 14, dPeriod: number = 3, smooth: number = 3): {
    time: Time;
    k: number;
    d: number;
  }[] {
    if (data.length < kPeriod) {
      return [];
    }

    const rawK: { time: Time; value: number }[] = [];

    for (let i = kPeriod - 1; i < data.length; i++) {
      const slice = data.slice(i - kPeriod + 1, i + 1);
      const highestHigh = Math.max(...slice.map(d => d.high));
      const lowestLow = Math.min(...slice.map(d => d.low));
      const range = highestHigh - lowestLow;
      const k = range === 0 ? 50 : ((data[i].close - lowestLow) / range) * 100;

      rawK.push({ time: data[i].time, value: Math.max(0, Math.min(100, k)) });
    }

    // Smooth %K with SMA (Fast Stochastic -> Slow Stochastic)
    const smoothedK: { time: Time; value: number }[] = [];
    for (let i = smooth - 1; i < rawK.length; i++) {
      const slice = rawK.slice(i - smooth + 1, i + 1);
      const avg = slice.reduce((sum, item) => sum + item.value, 0) / smooth;
      smoothedK.push({ time: rawK[i].time, value: avg });
    }

    // %D is SMA of smoothed %K
    const result: { time: Time; k: number; d: number }[] = [];
    for (let i = dPeriod - 1; i < smoothedK.length; i++) {
      const dSlice = smoothedK.slice(i - dPeriod + 1, i + 1);
      const dAvg = dSlice.reduce((sum, item) => sum + item.value, 0) / dPeriod;
      result.push({
        time: smoothedK[i].time,
        k: smoothedK[i].value,
        d: dAvg
      });
    }

    return result;
  }

  /**
   * Calculate Heikin-Ashi candlesticks
   */
  calculateHeikinAshi(data: ChartData[]): ChartData[] {
    if (data.length === 0) {
      return [];
    }

    const heikinAshiData: ChartData[] = [];

    for (let i = 0; i < data.length; i++) {
      const current = data[i];

      if (i === 0) {
        // First candle is the same as regular
        heikinAshiData.push({
          time: current.time,
          open: current.open,
          high: current.high,
          low: current.low,
          close: (current.open + current.high + current.low + current.close) / 4,
          volume: current.volume
        });
      } else {
        const previous = heikinAshiData[i - 1];
        const haClose = (current.open + current.high + current.low + current.close) / 4;
        const haOpen = (previous.open + previous.close) / 2;
        const haHigh = Math.max(current.high, haOpen, haClose);
        const haLow = Math.min(current.low, haOpen, haClose);

        heikinAshiData.push({
          time: current.time,
          open: haOpen,
          high: haHigh,
          low: haLow,
          close: haClose,
          volume: current.volume
        });
      }
    }

    return heikinAshiData;
  }
} 